import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ChatAttachment, ChatMessage, ChatMessageSegment, ChatStreamEvent } from '../models/chat.models';
import { ChatService } from './chat.service';
import { BudgetService } from 'src/app/pages/budget/services/budget.service';
import { BudgetDocumentService } from 'src/app/pages/budget/services/budget-document.service';
import { MyAccountService } from 'src/app/core/services/my-account.service';
import { TimezoneService } from 'src/app/core/utils/timezone.service';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';

/**
 * Payload contract for the send_budget tool when the backend hands the email
 * dispatch off to the frontend (Option A: chat reuses the platform's PDF
 * generation pipeline so the customer receives an identical email to the
 * one they would get from the budget detail page).
 */
interface PendingPdfEmailPayload {
  pendingPdfEmail?: boolean;
  budgetId?: number;
  recipientEmail?: string;
  validityDays?: number;
  clientFullName?: string | null;
  reason?: string;
}

/**
 * Drives a single SSE turn: appends user and assistant placeholder messages,
 * subscribes to the stream, builds segments (text + tool activity chips),
 * and marks completion or error.
 * Scoped (providers: [ChatTurnController]) on each host component so cancel
 * fires on destroy.
 *
 * Turn token pattern: every sendTurn() increments `turnToken` and captures
 * it in a closure. cancel() also increments the token. handleEvent() and
 * finish() ignore calls whose token doesn't match the current value, which
 * prevents a stale stream completion from corrupting a subsequently started
 * turn (e.g. user sends suggestion chips immediately after cancel/delete).
 */
@Injectable()
export class ChatTurnController implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private abort?: AbortController;
  private inflightMessageId: string | null = null;
  private inflightConvId: string | null = null;
  private currentAgentCode: string | null = null;

  /** Incremented on each new turn and on cancel. Used to discard stale callbacks. */
  private turnToken = 0;

  busy = false;

  /** Emits the agent code of the specialist currently responding, null when idle. */
  readonly activeAgentCode$ = new BehaviorSubject<string | null>(null);

  /** Stores the last sent turn so a retryable error can replay it. */
  private lastSentPayload: { convId: string; text: string; attachments?: ChatAttachment[] } | null = null;

  constructor(
    private readonly chatService: ChatService,
    private readonly translate: TranslateService,
    private readonly budgetService: BudgetService,
    private readonly budgetDocumentService: BudgetDocumentService,
    private readonly myAccountService: MyAccountService,
    private readonly timezoneService: TimezoneService,
    private readonly dateFormatLocalization: DateFormatLocalizationService,
  ) {}

  ngOnDestroy(): void {
    this.cancel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancel(): void {
    // Mark the in-flight assistant message as finished so it doesn't keep
    // showing a spinner after the turn is cancelled.
    if (this.inflightConvId && this.inflightMessageId) {
      this.chatService.updateMessage(this.inflightConvId, this.inflightMessageId, (m) => ({
        ...m,
        isStreaming: false,
      }));
    }
    // Clear inflight IDs before aborting so that any completion callback
    // that fires after the abort is a clean no-op.
    this.inflightMessageId = null;
    this.inflightConvId = null;
    this.currentAgentCode = null;
    this.activeAgentCode$.next(null);
    this.abort?.abort();
    this.abort = undefined;
    // Increment token so any pending stream callbacks (complete/error/next)
    // from the aborted turn are recognised as stale and ignored.
    this.turnToken++;
    this.busy = false;
  }

  sendTurn(conversationId: string, text: string, attachments?: ChatAttachment[]): void {
    if (this.busy) return;
    this.busy = true;
    this.lastSentPayload = { convId: conversationId, text, attachments };

    // Capture the token for this specific turn — used in handleEvent/finish
    // to guard against stale callbacks from a previously aborted stream.
    const token = ++this.turnToken;

    const userMessage: ChatMessage = {
      id: this.chatService.randomId(),
      role: 'user',
      content: text,
      attachments: attachments?.length ? attachments : undefined,
      createdAt: new Date().toISOString(),
    };
    this.chatService.appendMessage(conversationId, userMessage);

    // After persisting the user message, offer it to the auto-title logic.
    this.chatService.maybeAutoTitle(conversationId, text);

    // IMPORTANT: build the request before appending the assistant placeholder,
    // otherwise the empty placeholder would be sent to the backend (400).
    const request = this.chatService.buildSendRequest(conversationId);

    const assistantMessage: ChatMessage = {
      id: this.chatService.randomId(),
      role: 'assistant',
      content: '',
      segments: [{ kind: 'text', content: '' }],
      createdAt: new Date().toISOString(),
      isStreaming: true,
    };
    this.chatService.appendMessage(conversationId, assistantMessage);
    this.inflightMessageId = assistantMessage.id;
    this.inflightConvId = conversationId;

    this.abort = new AbortController();

    this.chatService
      .sendMessage(request, this.abort.signal)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next:     (event) => this.handleEvent(event, token),
        complete: ()      => this.finish(token),
        error:    ()      => this.finish(token),
      });
  }

  private handleEvent(event: ChatStreamEvent, token: number): void {
    // Discard events from a turn that was cancelled/superseded
    if (token !== this.turnToken) return;
    if (!this.inflightConvId || !this.inflightMessageId) return;

    switch (event.type) {
      case 'text_delta':
        this.chatService.updateMessage(this.inflightConvId, this.inflightMessageId, (m) => {
          const segments = [...(m.segments ?? [{ kind: 'text' as const, content: '' }])];
          const last = segments[segments.length - 1];
          if (last.kind === 'text') {
            segments[segments.length - 1] = { ...last, content: last.content + event.text };
          } else {
            segments.push({ kind: 'text', content: event.text });
          }
          return { ...m, content: m.content + event.text, segments, isStreaming: true };
        });
        break;

      case 'tool_use_start': {
        const i18nKey = `chatAi.tools.${event.toolName}`;
        const translated = this.translate.instant(i18nKey) as string;
        const label = translated !== i18nKey ? translated : event.toolName;
        this.chatService.updateMessage(this.inflightConvId, this.inflightMessageId, (m) => {
          const segments = [...(m.segments ?? [])];
          segments.push({
            kind: 'tool_activity',
            content: label,
            toolName: event.toolName,
            toolId: event.toolId,
            toolStatus: 'running',
          });
          segments.push({ kind: 'text', content: '' });
          return { ...m, segments, isStreaming: true };
        });
        break;
      }

      case 'tool_result': {
        this.chatService.updateMessage(this.inflightConvId, this.inflightMessageId, (m) => {
          const segments = (m.segments ?? []).map((seg) => {
            if (seg.kind === 'tool_activity' && seg.toolId === event.toolId) {
              return { ...seg, toolStatus: event.isError ? 'error' as const : 'done' as const };
            }
            return seg;
          });
          return { ...m, segments };
        });

        // send_budget hands off the actual dispatch to the frontend so the chat
        // reuses the same PDF + email pipeline as the budget detail page.
        if (!event.isError) {
          const payload = event.payload as PendingPdfEmailPayload | null | undefined;
          if (payload?.pendingPdfEmail === true && payload.budgetId) {
            this.handlePendingPdfEmail(payload, this.inflightConvId);
          }
        }
        break;
      }

      case 'error': {
        const friendly = this.getFriendlyErrorMessage(event.errorCode, event.message);
        const retryPayload = event.retryable && this.lastSentPayload
          ? { text: this.lastSentPayload.text, attachments: this.lastSentPayload.attachments }
          : undefined;
        this.chatService.updateMessage(this.inflightConvId, this.inflightMessageId, (m) => ({
          ...m,
          content: friendly,
          segments: [{ kind: 'text', content: friendly }],
          role: 'system',
          isError: true,
          isStreaming: false,
          retryPayload,
        }));
        break;
      }

      case 'agent_active':
        this.currentAgentCode = event.agentCode;
        this.activeAgentCode$.next(event.agentCode);
        this.chatService.updateMessage(this.inflightConvId, this.inflightMessageId, (m) => ({
          ...m,
          agentCode: event.agentCode,
        }));
        break;

      case 'message_stop':
        this.chatService.updateMessage(this.inflightConvId, this.inflightMessageId, (m) => ({
          ...m,
          isStreaming: false,
        }));
        break;

      default:
        break;
    }
  }

  /**
   * Frontend-side dispatch for the send_budget tool. Mirrors the budget detail
   * page flow:
   *   1. Fetch the full Budget and the center info.
   *   2. Generate the PDF via BudgetDocumentService.getPDFtoSend (same template
   *      the customer would receive from the web UI).
   *   3. POST to /Budgets/{id}/sent which marks SENT + dispatches the email.
   *   4. Append a system message in the conversation reporting the outcome.
   * Runs async — the SSE stream keeps flowing while this works in the background.
   */
  private async handlePendingPdfEmail(payload: PendingPdfEmailPayload, conversationId: string): Promise<void> {
    const budgetId = payload.budgetId!;
    const recipientEmail = payload.recipientEmail ?? '';
    const validityDays = payload.validityDays && payload.validityDays > 0 ? payload.validityDays : 15;

    try {
      const [budget, centerInfo] = await Promise.all([
        firstValueFrom(this.budgetService.getBudgetById(budgetId)),
        firstValueFrom(this.myAccountService.getCenterInfo()),
      ]);

      if (!budget) {
        this.appendSystemMessage(conversationId, `⚠️ No se pudo recuperar el presupuesto #${budgetId} para generar el PDF.`);
        return;
      }
      if (!centerInfo) {
        this.appendSystemMessage(conversationId, `⚠️ No se pudo recuperar la información del centro para generar el PDF del presupuesto #${budgetId}.`);
        return;
      }

      const validUntil = moment().startOf('day').add(validityDays, 'days').toDate();
      const pdfBase64 = await this.budgetDocumentService.getPDFtoSend(budget, validUntil, centerInfo);

      await firstValueFrom(
        this.budgetService.sendBudgetEmail(
          budgetId,
          moment(validUntil).format('YYYY-MM-DD'),
          pdfBase64,
        ),
      );

      const recipient = recipientEmail || budget.client?.email || '';
      const recipientLabel = recipient ? ` a ${recipient}` : '';
      const country = this.dateFormatLocalization.getCurrentCountryCode();
      const validUntilLabel = this.timezoneService.formatDateForCenter(validUntil, country, {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: undefined, minute: undefined, second: undefined,
      });
      this.appendSystemMessage(
        conversationId,
        `✅ Presupuesto #${budgetId} enviado correctamente${recipientLabel} (válido hasta ${validUntilLabel}).`,
      );
    } catch (err: any) {
      console.error('[chat] send_budget PDF dispatch failed', err);
      const reason = err?.error?.message ?? err?.message ?? 'Error desconocido';
      this.appendSystemMessage(
        conversationId,
        `⚠️ El presupuesto #${budgetId} se ha marcado como ENVIADO pero no se pudo entregar el email: ${reason}. Revisa la dirección y vuelve a intentarlo desde la pantalla del presupuesto.`,
      );
    }
  }

  /** Pushes a system-role message into the conversation. Used by post-stream actions. */
  private appendSystemMessage(conversationId: string, text: string): void {
    this.chatService.appendMessage(conversationId, {
      id: this.chatService.randomId(),
      role: 'system',
      content: text,
      segments: [{ kind: 'text', content: text }],
      createdAt: new Date().toISOString(),
    });
  }

  private getFriendlyErrorMessage(errorCode: string, rawMessage: string): string {
    const minutesMatch = rawMessage.match(/(\d+)\s*minute/i);
    const minutes = minutesMatch ? minutesMatch[1] : '2';
    const keyMap: Record<string, string> = {
      'CHAT_RATE_LIMITED':    'chatAi.errors.rateLimited',
      'CHAT_TIMEOUT':         'chatAi.errors.timeout',
      'CHAT_FEATURE_DISABLED':'chatAi.errors.featureDisabled',
      'CHAT_UNAUTHENTICATED': 'chatAi.errors.unauthenticated',
    };
    const key = keyMap[errorCode];
    if (key) {
      return this.translate.instant(key, { minutes }) as string;
    }
    // For upstream / unhandled errors, show the backend's raw message so the
    // operator can act on it (Bedrock body, Anthropic 4xx body, etc.). Falls
    // back to the generic translation only when the backend didn't send anything.
    const trimmed = (rawMessage ?? '').trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
    return this.translate.instant('chatAi.errors.generic') as string;
  }

  private finish(token: number): void {
    // If this token no longer matches, the turn was cancelled and a new one
    // may have already started — do nothing to avoid corrupting the new turn.
    if (token !== this.turnToken) return;

    if (this.inflightConvId && this.inflightMessageId) {
      this.chatService.updateMessage(this.inflightConvId, this.inflightMessageId, (m) => ({
        ...m,
        isStreaming: false,
      }));
      this.extractAndApplyTitle(this.inflightConvId, this.inflightMessageId);
    }
    this.inflightMessageId = null;
    this.inflightConvId = null;
    this.currentAgentCode = null;
    this.activeAgentCode$.next(null);
    this.abort = undefined;
    this.busy = false;
  }

  private extractAndApplyTitle(convId: string, msgId: string): void {
    const convo = this.chatService.getConversation(convId);
    const msg = convo?.messages.find((m) => m.id === msgId);
    if (!msg) return;
    const titleMatch = /```title\s*\n([\s\S]*?)\n```/.exec(msg.content);
    if (titleMatch) {
      const newTitle = titleMatch[1].trim().slice(0, 50);
      if (newTitle.length > 0) {
        this.chatService.renameConversation(convId, newTitle);
      }
    }
  }
}
